// Theme preference, resolution and application.
//
// Deliberately device-local rather than a per-user Firestore setting like
// everything else in ContSetari: the value has to be readable before first
// paint (the marketing routes are prerendered, so an async read is a
// guaranteed white flash) and it has to work signed out.
//
// The `system` mode is resolved to an explicit `light` / `dark` attribute
// here and in the pre-paint script in index.html, which is why globals.css
// needs only one dark block and no prefers-color-scheme duplicate.

import { useCallback, useEffect, useState } from "react";

export type ThemeMode = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "aa-theme";

/** Light: the --primary token (the browser chrome is indigo); dark: --background. Kept in step with globals.css. */
export const THEME_COLOR_LIGHT = "#24389C";
export const THEME_COLOR_DARK = "#0B0D18";

export function isThemeMode(value: unknown): value is ThemeMode {
  return value === "system" || value === "light" || value === "dark";
}

export function readThemeMode(): ThemeMode {
  if (typeof window === "undefined") return "system";
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeMode(raw) ? raw : "system";
  } catch {
    // Private-mode Safari and blocked site data both throw here.
    return "system";
  }
}

export function writeThemeMode(mode: ThemeMode): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // The choice applies for this session and simply does not persist.
  }
}

export function resolveTheme(
  mode: ThemeMode,
  prefersDark: boolean,
): ResolvedTheme {
  if (mode === "system") return prefersDark ? "dark" : "light";
  return mode;
}

export function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", resolved);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute(
      "content",
      resolved === "dark" ? THEME_COLOR_DARK : THEME_COLOR_LIGHT,
    );
  }
}

const DARK_QUERY = "(prefers-color-scheme: dark)";

function prefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia(DARK_QUERY).matches;
}

/**
 * The live theme. `mode` is what the user chose; `resolved` is what is on
 * screen. Pinning a mode stops the OS from having any say; `system` follows it,
 * including a change made while the tab is open.
 */
export function useTheme(): {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
} {
  const [mode, setModeState] = useState<ThemeMode>(readThemeMode);
  const [systemDark, setSystemDark] = useState<boolean>(prefersDark);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(DARK_QUERY);
    const onChange = (e: MediaQueryListEvent | { matches: boolean }) =>
      setSystemDark(e.matches);
    mq.addEventListener("change", onChange as (e: MediaQueryListEvent) => void);
    return () =>
      mq.removeEventListener(
        "change",
        onChange as (e: MediaQueryListEvent) => void,
      );
  }, []);

  const resolved = resolveTheme(mode, systemDark);

  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const setMode = useCallback((next: ThemeMode) => {
    writeThemeMode(next);
    setModeState(next);
  }, []);

  return { mode, resolved, setMode };
}
